export type InstrumentalMode = 'flame' | 'ions' | 'colorimetry' | 'aas' | 'gravimetric';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const INSTRUMENTAL_ANALYSIS_TOOL_IDENTITY = {
  id: 'instrumental-analysis',
  toolKey: 'chemistry-instrumental-analysis',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const INSTRUMENTAL_MODES: readonly InstrumentalMode[] = [
  'flame', 'ions', 'colorimetry', 'aas', 'gravimetric',
];

/**
 * Default mode by learner Year (CT-MODE). The tool starts at Year 11 with flame tests; a Year 12
 * view opens on quantitative calibration. The learner can still switch to any mode.
 */
export const INSTRUMENTAL_MODE_YEARS: readonly { mode: InstrumentalMode; fromYear: number }[] = [
  { mode: 'flame', fromYear: 11 },
  { mode: 'colorimetry', fromYear: 12 },
];

export type ReviewStatus = 'pending-teaching-review';

export interface DataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: ReviewStatus;
}

// ---- Flame tests and emission spectra ----

export const VISIBLE_MIN_NM = 380;
export const VISIBLE_MAX_NM = 780;
/** Two lines closer than this are treated as the same line when matching spectra. */
export const LINE_MATCH_TOLERANCE_NM = 0.5;
/** hc expressed in eV nm, for photon energy E = hc / λ. */
export const HC_EV_NM = 1239.84;

export type FlameColourId =
  'red' | 'yellow-orange' | 'lilac' | 'brick-red' | 'apple-green' | 'blue-green' | 'blue-violet';

export const FLAME_COLOUR_LABELS: Record<FlameColourId, string> = {
  red: 'Red (crimson)',
  'yellow-orange': 'Yellow-orange',
  lilac: 'Lilac',
  'brick-red': 'Orange-red (brick red)',
  'apple-green': 'Pale apple green',
  'blue-green': 'Blue-green',
  'blue-violet': 'Blue-violet',
};

export const FLAME_COLOUR_OPTIONS: readonly FlameColourId[] = [
  'red', 'yellow-orange', 'lilac', 'brick-red', 'apple-green', 'blue-green', 'blue-violet',
];

export type EmitterId =
  'lithium' | 'sodium' | 'potassium' | 'calcium' | 'strontium' | 'barium' | 'copper' | 'caesium'
  | 'hydrogen';

export interface Emitter extends DataProvenance {
  id: EmitterId;
  /** Species placed in the flame, or the gas in a discharge tube. */
  symbol: string;
  name: string;
  source: 'flame' | 'discharge-tube';
  flameColour: FlameColourId | null;
  /** Representative wavelengths used only to tint the generated flame swatch. */
  swatchWavelengthsNm: number[];
  /** Major visible lines in air, nm, rounded to 0.1 nm. */
  linesNm: number[];
  /** Line used for the energy-level example. */
  mainLineNm: number;
  colourNote: string;
}

const SPECTRA_SOURCES = [
  'NIST Atomic Spectra Database (ASD), strong lines of neutral and singly ionised atoms',
  'CRC Handbook of Chemistry and Physics, line spectra of the elements',
];
const FLAME_COLOUR_SOURCE = 'Standard senior chemistry flame-test colours (non-luminous Bunsen flame)';
const FLAME_CONDITIONS =
  'Salt on a clean nichrome or platinum wire in a non-luminous Bunsen flame. Wavelengths are in air, nm, rounded to 0.1 nm; only major visible lines are listed.';

type FlameEntry = Pick<
  Emitter,
  'id' | 'symbol' | 'name' | 'flameColour' | 'swatchWavelengthsNm' | 'linesNm' | 'mainLineNm' | 'colourNote'
>;

const flameEmitter = (entry: FlameEntry): Emitter => ({
  ...entry,
  source: 'flame',
  sources: [...SPECTRA_SOURCES, FLAME_COLOUR_SOURCE],
  conditions: FLAME_CONDITIONS,
  reviewStatus: 'pending-teaching-review',
});

export const EMITTERS: Emitter[] = [
  flameEmitter({
    id: 'lithium', symbol: 'Li⁺', name: 'Lithium ion', flameColour: 'red',
    swatchWavelengthsNm: [670.8], linesNm: [610.4, 670.8], mainLineNm: 670.8,
    colourNote: 'Crimson. It can look like strontium by eye; the 670.8 nm line tells them apart.',
  }),
  flameEmitter({
    id: 'sodium', symbol: 'Na⁺', name: 'Sodium ion', flameColour: 'yellow-orange',
    swatchWavelengthsNm: [589.0], linesNm: [589.0, 589.6], mainLineNm: 589.0,
    colourNote: 'Intense and persistent. A trace of sodium contamination can hide other colours.',
  }),
  flameEmitter({
    id: 'potassium', symbol: 'K⁺', name: 'Potassium ion', flameColour: 'lilac',
    swatchWavelengthsNm: [404.4, 766.5], linesNm: [404.4, 766.5, 769.9], mainLineNm: 766.5,
    colourNote:
      'Faint lilac from violet and deep-red lines together. Cobalt-blue glass filters out sodium yellow.',
  }),
  flameEmitter({
    id: 'calcium', symbol: 'Ca²⁺', name: 'Calcium ion', flameColour: 'brick-red',
    swatchWavelengthsNm: [620], linesNm: [422.7, 558.9, 612.2, 616.2, 643.9], mainLineNm: 422.7,
    colourNote:
      'Brick red. Much of the flame colour comes from molecular bands (such as CaOH), not only the atomic lines shown.',
  }),
  flameEmitter({
    id: 'strontium', symbol: 'Sr²⁺', name: 'Strontium ion', flameColour: 'red',
    swatchWavelengthsNm: [650], linesNm: [460.7, 640.8, 707.0], mainLineNm: 460.7,
    colourNote:
      'Red (crimson). Molecular bands (such as SrOH) give much of the red; compare lines with lithium.',
  }),
  flameEmitter({
    id: 'barium', symbol: 'Ba²⁺', name: 'Barium ion', flameColour: 'apple-green',
    swatchWavelengthsNm: [530], linesNm: [455.4, 493.4, 553.5], mainLineNm: 553.5,
    colourNote: 'Pale apple green. Barium compounds are toxic; this is a teacher-supervised test.',
  }),
  flameEmitter({
    id: 'copper', symbol: 'Cu²⁺', name: 'Copper(II) ion', flameColour: 'blue-green',
    swatchWavelengthsNm: [500], linesNm: [510.6, 515.3, 521.8], mainLineNm: 521.8,
    colourNote: 'Blue-green. The exact shade depends on the anion present, for example chloride.',
  }),
  flameEmitter({
    id: 'caesium', symbol: 'Cs⁺', name: 'Caesium ion', flameColour: 'blue-violet',
    swatchWavelengthsNm: [455.5], linesNm: [455.5, 459.3], mainLineNm: 455.5,
    colourNote:
      'Blue-violet. Its 455.5 nm line sits beside a barium line, so match the whole pattern.',
  }),
  {
    id: 'hydrogen',
    symbol: 'H',
    name: 'Hydrogen (Balmer series)',
    source: 'discharge-tube',
    flameColour: null,
    swatchWavelengthsNm: [656.3, 486.1],
    linesNm: [410.2, 434.0, 486.1, 656.3],
    mainLineNm: 656.3,
    colourNote:
      'Reference spectrum from a hydrogen discharge tube, not a flame test. Electrons fall to the n = 2 level.',
    sources: SPECTRA_SOURCES,
    conditions: 'Low-pressure hydrogen discharge tube. Wavelengths in air, nm, rounded to 0.1 nm.',
    reviewStatus: 'pending-teaching-review',
  },
];

export const FLAME_EMITTERS = EMITTERS.filter((emitter) => emitter.source === 'flame');

export function emitterById(id: string): Emitter | null {
  return EMITTERS.find((emitter) => emitter.id === id) ?? null;
}

export interface FlameUnknown {
  id: string;
  label: string;
  emitterId: EmitterId;
}

/** Unknown spectra in a fixed, scrambled order. Each shows every listed line of one emitter. */
export const FLAME_UNKNOWNS: FlameUnknown[] = (
  [
    'barium', 'sodium', 'strontium', 'caesium', 'potassium', 'hydrogen', 'lithium', 'copper',
    'calcium',
  ] as EmitterId[]
).map((emitterId, index) => ({
  id: `spectrum-${index + 1}`,
  label: `Unknown spectrum ${index + 1}`,
  emitterId,
}));

export function unknownSpectrumLines(unknown: FlameUnknown): number[] {
  const emitter = emitterById(unknown.emitterId);
  if (!emitter) throw new Error(`Unknown emitter ${unknown.emitterId}`);
  return [...emitter.linesNm];
}

export function photonEnergyEv(wavelengthNm: number): number {
  if (!Number.isFinite(wavelengthNm) || wavelengthNm <= 0) {
    throw new Error('Wavelength must be a positive number of nanometres');
  }
  return HC_EV_NM / wavelengthNm;
}

function hasLine(lines: number[], target: number) {
  return lines.some((line) => Math.abs(line - target) <= LINE_MATCH_TOLERANCE_NM);
}

/** `unexplained`: unknown lines the reference lacks; `missing`: reference lines not seen. */
export interface LineComparison { matched: number[]; unexplained: number[]; missing: number[] }

export function compareLines(unknownLines: number[], referenceLines: number[]): LineComparison {
  return {
    matched: unknownLines.filter((line) => hasLine(referenceLines, line)),
    unexplained: unknownLines.filter((line) => !hasLine(referenceLines, line)),
    missing: referenceLines.filter((line) => !hasLine(unknownLines, line)),
  };
}

/**
 * Returns the single emitter whose full line pattern matches. Fails closed (null) when no
 * reference or more than one reference matches.
 */
export function matchSpectrum(lines: number[]): EmitterId | null {
  if (lines.length === 0) return null;
  const matches = EMITTERS.filter((emitter) => {
    const comparison = compareLines(lines, emitter.linesNm);
    return comparison.unexplained.length === 0 && comparison.missing.length === 0;
  });
  return matches.length === 1 ? matches[0].id : null;
}

const formatNm = (lines: number[]) => lines.map((line) => line.toFixed(1)).join(', ');

export function checkFlamePrediction(emitter: Emitter, prediction: FlameColourId) {
  if (emitter.flameColour === null) {
    throw new Error(`${emitter.name} is not a flame-test case`);
  }
  const correct = prediction === emitter.flameColour;
  const expectedLabel = FLAME_COLOUR_LABELS[emitter.flameColour];
  const others = FLAME_EMITTERS.filter(
    (candidate) => candidate.flameColour === prediction && candidate.id !== emitter.id,
  );
  return {
    correct,
    expected: emitter.flameColour,
    feedback: correct
      ? `Correct: ${emitter.symbol} gives a ${expectedLabel.toLowerCase()} flame. ${emitter.colourNote}`
      : `${emitter.symbol} gives a ${expectedLabel.toLowerCase()} flame, not ${FLAME_COLOUR_LABELS[
          prediction
        ].toLowerCase()}.${
          others.length > 0
            ? ` ${FLAME_COLOUR_LABELS[prediction]} is the flame colour of ${others
                .map((candidate) => candidate.symbol)
                .join(' and ')}.`
            : ''
        } Each element emits its own set of wavelengths because its electron energy levels are different.`,
  };
}

export function checkSpectrumIdentification(unknown: FlameUnknown, guess: EmitterId) {
  const lines = unknownSpectrumLines(unknown);
  const actual = emitterById(unknown.emitterId);
  const guessed = emitterById(guess);
  if (!actual || !guessed) throw new Error('Unsupported emitter');
  const correct = matchSpectrum(lines) === guess;
  if (correct) {
    return {
      correct,
      expected: actual.id,
      feedback: `Correct: every line (${formatNm(lines)} nm) matches the ${actual.name.toLowerCase()} reference, and no reference line is missing.`,
    };
  }
  const comparison = compareLines(lines, guessed.linesNm);
  const parts: string[] = [];
  if (comparison.unexplained.length > 0) {
    parts.push(`the unknown has ${formatNm(comparison.unexplained)} nm, which ${guessed.symbol} does not emit`);
  }
  if (comparison.missing.length > 0) {
    parts.push(`${guessed.symbol} would also show ${formatNm(comparison.missing)} nm, which is absent`);
  }
  return {
    correct,
    expected: actual.id,
    feedback: `Not ${guessed.name.toLowerCase()}: ${parts.join('; ')}. Every line must match, so this is ${actual.name.toLowerCase()} (${formatNm(actual.linesNm)} nm).`,
  };
}

// ---- Qualitative ion tests ----

export type IonId =
  'chloride' | 'bromide' | 'iodide' | 'sulfate' | 'carbonate' | 'iron-ii' | 'iron-iii'
  | 'copper-ii' | 'ammonium';

export type IonTestId = 'nitric-acid' | 'silver-nitrate' | 'barium-chloride' | 'sodium-hydroxide';

export type ObservationColour =
  'none' | 'white' | 'cream' | 'yellow' | 'pale-yellow' | 'green' | 'red-brown' | 'blue' | 'brown'
  | 'milky';

export const OBSERVATION_COLOUR_LABELS: Record<ObservationColour, string> = {
  none: 'No visible change',
  white: 'White',
  cream: 'Cream',
  yellow: 'Yellow',
  'pale-yellow': 'Pale yellow-white',
  green: 'Green',
  'red-brown': 'Red-brown',
  blue: 'Pale blue',
  brown: 'Brown',
  milky: 'Limewater turns milky',
};

export interface IonTest {
  id: IonTestId;
  name: string;
  reagent: string;
  method: string;
}

const ionTest = (id: IonTestId, name: string, reagent: string, method: string): IonTest => ({
  id, name, reagent, method,
});

export const ION_TESTS: IonTest[] = [
  ionTest('nitric-acid', 'Dilute nitric acid', 'HNO₃(aq)',
    'Add dilute nitric acid. Bubble any gas through limewater.'),
  ionTest('silver-nitrate', 'Silver nitrate', 'AgNO₃(aq)', 'Add a few drops of silver nitrate solution.'),
  ionTest('barium-chloride', 'Barium chloride', 'BaCl₂(aq)',
    'Add a few drops of barium chloride solution.'),
  ionTest('sodium-hydroxide', 'Sodium hydroxide, then warm', 'NaOH(aq)',
    'Add sodium hydroxide solution, warm gently and hold damp red litmus at the mouth.'),
];

export interface IonPositive {
  test: IonTestId;
  colour: ObservationColour;
  observation: string;
  equation: string;
}

export interface IonCase extends DataProvenance {
  id: IonId;
  symbol: string;
  name: string;
  positive: IonPositive;
}

const ION_SOURCES = [
  'Standard senior chemistry qualitative-analysis tables (precipitate colours and gas tests)',
  'IUPAC Nomenclature of Inorganic Chemistry (formulae and charges)',
];
const ION_CONDITIONS =
  'Dilute aqueous solutions at about 25 °C. Spectator ions in each sample do not react in these tests.';

function ionCase(
  id: IonId,
  symbol: string,
  name: string,
  positive: IonPositive,
): IonCase {
  return {
    id,
    symbol,
    name,
    positive,
    sources: ION_SOURCES,
    conditions: ION_CONDITIONS,
    reviewStatus: 'pending-teaching-review',
  };
}

export const ION_CASES: IonCase[] = [
  ionCase('chloride', 'Cl⁻', 'Chloride', {
    test: 'silver-nitrate', colour: 'white', observation: 'White precipitate of silver chloride.',
    equation: 'Ag⁺(aq) + Cl⁻(aq) → AgCl(s)',
  }),
  ionCase('bromide', 'Br⁻', 'Bromide', {
    test: 'silver-nitrate', colour: 'cream', observation: 'Cream precipitate of silver bromide.',
    equation: 'Ag⁺(aq) + Br⁻(aq) → AgBr(s)',
  }),
  ionCase('iodide', 'I⁻', 'Iodide', {
    test: 'silver-nitrate', colour: 'yellow', observation: 'Yellow precipitate of silver iodide.',
    equation: 'Ag⁺(aq) + I⁻(aq) → AgI(s)',
  }),
  ionCase('sulfate', 'SO₄²⁻', 'Sulfate', {
    test: 'barium-chloride', colour: 'white', observation: 'White precipitate of barium sulfate.',
    equation: 'Ba²⁺(aq) + SO₄²⁻(aq) → BaSO₄(s)',
  }),
  ionCase('carbonate', 'CO₃²⁻', 'Carbonate', {
    test: 'nitric-acid', colour: 'milky',
    observation: 'Effervescence. The carbon dioxide turns limewater milky.',
    equation: 'CO₃²⁻(aq) + 2H⁺(aq) → CO₂(g) + H₂O(l)',
  }),
  ionCase('iron-ii', 'Fe²⁺', 'Iron(II)', {
    test: 'sodium-hydroxide', colour: 'green',
    observation: 'Green precipitate of iron(II) hydroxide. It slowly turns brown at the surface in air.',
    equation: 'Fe²⁺(aq) + 2OH⁻(aq) → Fe(OH)₂(s)',
  }),
  ionCase('iron-iii', 'Fe³⁺', 'Iron(III)', {
    test: 'sodium-hydroxide', colour: 'red-brown',
    observation: 'Red-brown precipitate of iron(III) hydroxide.',
    equation: 'Fe³⁺(aq) + 3OH⁻(aq) → Fe(OH)₃(s)',
  }),
  ionCase('copper-ii', 'Cu²⁺', 'Copper(II)', {
    test: 'sodium-hydroxide', colour: 'blue',
    observation: 'Pale blue precipitate of copper(II) hydroxide.',
    equation: 'Cu²⁺(aq) + 2OH⁻(aq) → Cu(OH)₂(s)',
  }),
  ionCase('ammonium', 'NH₄⁺', 'Ammonium', {
    test: 'sodium-hydroxide', colour: 'none',
    observation: 'No precipitate. On warming, ammonia gas turns damp red litmus blue.',
    equation: 'NH₄⁺(aq) + OH⁻(aq) → NH₃(g) + H₂O(l)',
  }),
];

export function ionById(id: string): IonCase | null {
  return ION_CASES.find((ion) => ion.id === id) ?? null;
}

export interface IonSample {
  id: string;
  label: string;
  ionId: IonId;
}

/** Unknown samples in a fixed, scrambled order. */
export const ION_SAMPLES: IonSample[] = (
  [
    'iodide', 'iron-iii', 'carbonate', 'ammonium', 'chloride', 'copper-ii', 'sulfate', 'iron-ii',
    'bromide',
  ] as IonId[]
).map((ionId, index) => ({
  id: `sample-${String.fromCharCode(65 + index).toLowerCase()}`,
  label: `Sample ${String.fromCharCode(65 + index)}`,
  ionId,
}));

export const MAX_ION_STEPS = ION_TESTS.length;

export type IonStepKind = 'positive' | 'negative' | 'interference' | 'stopped';

export interface IonStepResult {
  test: IonTestId;
  kind: IonStepKind;
  colour: ObservationColour;
  precipitate: boolean;
  observation: string;
  equation?: string;
  /** Plain-language warning when the result comes from the method, not the sample. */
  interference?: string;
}

export interface IonRun {
  ok: boolean;
  error?: string;
  steps: IonStepResult[];
}

interface TubeState {
  carbonatePresent: boolean;
  hydroxideExcess: boolean;
  silverExcess: boolean;
  chlorideAdded: boolean;
  precipitate: boolean;
}

const HALIDES: IonId[] = ['chloride', 'bromide', 'iodide'];

function negative(test: IonTestId, observation: string): IonStepResult {
  return { test, kind: 'negative', colour: 'none', precipitate: false, observation };
}

const SILVER_OXIDE = {
  colour: 'brown',
  observation: 'Brown precipitate of silver oxide.',
  equation: '2Ag⁺(aq) + 2OH⁻(aq) → Ag₂O(s) + H₂O(l)',
} as const;
const SILVER_CHLORIDE = {
  colour: 'white',
  observation: 'White precipitate.',
  equation: 'Ag⁺(aq) + Cl⁻(aq) → AgCl(s)',
} as const;

/** A precipitate caused by the method rather than the target ion. */
function interference(
  test: IonTestId,
  tube: TubeState,
  result: { colour: ObservationColour; observation: string; equation: string },
  warning: string,
): IonStepResult {
  tube.precipitate = true;
  return { test, kind: 'interference', precipitate: true, ...result, interference: warning };
}

function positive(ion: IonCase, test: IonTestId, tube: TubeState): IonStepResult {
  const precipitate = ion.positive.test !== 'nitric-acid' && ion.positive.colour !== 'none';
  tube.precipitate = precipitate;
  return {
    test,
    kind: 'positive',
    colour: ion.positive.colour,
    precipitate,
    observation: ion.positive.observation,
    equation: ion.positive.equation,
  };
}

const NO_PRECIPITATE = 'No precipitate. The solution stays clear.';

function runStep(ion: IonCase, test: IonTestId, tube: TubeState): IonStepResult {
  if (tube.precipitate) {
    return {
      test,
      kind: 'stopped',
      colour: 'none',
      precipitate: true,
      observation:
        'Not run. A precipitate is already in this portion and would hide a new observation. Use a fresh portion of the sample.',
    };
  }
  switch (test) {
    case 'nitric-acid': {
      tube.hydroxideExcess = false;
      if (!tube.carbonatePresent) return negative(test, 'No effervescence. The limewater stays clear.');
      tube.carbonatePresent = false;
      return positive(ion, test, tube);
    }
    case 'silver-nitrate': {
      if (tube.hydroxideExcess) {
        return interference(test, tube, SILVER_OXIDE,
          'Hydroxide left from the sodium hydroxide test reacts with silver ions and hides any halide precipitate. Acidify with dilute nitric acid first.');
      }
      if (tube.chlorideAdded) {
        return interference(test, tube, SILVER_CHLORIDE,
          'The chloride came from the barium chloride reagent, not necessarily from the sample. Test for halides on a fresh portion.');
      }
      if (HALIDES.includes(ion.id)) return positive(ion, test, tube);
      if (tube.carbonatePresent) {
        return interference(test, tube, {
          colour: 'pale-yellow',
          observation: 'Pale yellow-white precipitate of silver carbonate.',
          equation: '2Ag⁺(aq) + CO₃²⁻(aq) → Ag₂CO₃(s)',
        }, 'Carbonate also precipitates silver ions, so this is not evidence of a halide. Add dilute nitric acid first to remove carbonate.');
      }
      tube.silverExcess = true;
      return negative(test, NO_PRECIPITATE);
    }
    case 'barium-chloride': {
      tube.chlorideAdded = true;
      if (tube.silverExcess) {
        return interference(test, tube, SILVER_CHLORIDE,
          'Silver ions left from the silver nitrate test react with the chloride in the reagent. Test for sulfate on a fresh portion.');
      }
      if (tube.carbonatePresent) {
        return interference(test, tube, {
          colour: 'white',
          observation: 'White precipitate of barium carbonate.',
          equation: 'Ba²⁺(aq) + CO₃²⁻(aq) → BaCO₃(s)',
        }, 'Carbonate also gives a white barium precipitate, so this is not evidence of sulfate. Acidify with dilute nitric acid first.');
      }
      return ion.positive.test === test ? positive(ion, test, tube) : negative(test, NO_PRECIPITATE);
    }
    case 'sodium-hydroxide': {
      tube.hydroxideExcess = true;
      if (tube.silverExcess) {
        return interference(test, tube, SILVER_OXIDE,
          'Silver ions left from the silver nitrate test react with hydroxide. Test cations on a fresh portion.');
      }
      return ion.positive.test === test
        ? positive(ion, test, tube)
        : negative(test, 'No precipitate. On warming, damp red litmus stays red.');
    }
    default:
      throw new Error(`Unsupported test ${String(test)}`);
  }
}

/**
 * Runs an ordered plan of tests on ONE portion of the sample. Fails closed for an unknown ion,
 * an unknown test, a repeated test or an empty plan.
 */
export function runIonTests(ionId: string, plan: string[]): IonRun {
  const ion = ionById(ionId);
  if (!ion) return { ok: false, error: 'This sample is not in the reviewed case set.', steps: [] };
  if (plan.length === 0) return { ok: false, error: 'Choose at least one test.', steps: [] };
  if (plan.length > MAX_ION_STEPS) {
    return { ok: false, error: `Choose at most ${MAX_ION_STEPS} tests.`, steps: [] };
  }
  const known = new Set(ION_TESTS.map((test) => test.id as string));
  if (plan.some((test) => !known.has(test))) {
    return { ok: false, error: 'This plan includes a test the model does not support.', steps: [] };
  }
  if (new Set(plan).size !== plan.length) {
    return { ok: false, error: 'Each test can be used once per portion.', steps: [] };
  }
  const tube: TubeState = {
    carbonatePresent: ion.id === 'carbonate',
    hydroxideExcess: false,
    silverExcess: false,
    chlorideAdded: false,
    precipitate: false,
  };
  return {
    ok: true,
    steps: (plan as IonTestId[]).map((test) => runStep(ion, test, tube)),
  };
}

export function testName(test: IonTestId): string {
  return ION_TESTS.find((candidate) => candidate.id === test)?.name ?? test;
}

export function checkIonIdentification(sample: IonSample, guess: IonId, run: IonRun) {
  const actual = ionById(sample.ionId);
  const guessed = ionById(guess);
  if (!actual || !guessed) throw new Error('Unsupported ion');
  const confirmed = run.ok && run.steps.some(
    (step) => step.kind === 'positive' && step.test === actual.positive.test,
  );
  if (guess === actual.id) {
    return {
      correct: true,
      confirmed,
      expected: actual.id,
      feedback: confirmed
        ? `Correct: ${actual.symbol}. Your ${testName(actual.positive.test).toLowerCase()} test confirmed it: ${actual.positive.observation}`
        : `Correct ion, but your plan did not confirm it. The confirming test for ${actual.symbol} is ${testName(actual.positive.test).toLowerCase()}: ${actual.positive.observation}`,
    };
  }
  return {
    correct: false,
    confirmed,
    expected: actual.id,
    feedback: `Not ${guessed.symbol}. ${guessed.symbol} would give this with ${testName(
      guessed.positive.test,
    ).toLowerCase()}: ${guessed.positive.observation} This sample is ${actual.symbol}: ${actual.positive.observation}`,
  };
}

// ---- Calibration: colorimetry (Beer–Lambert) and AAS ----

export type CalibrationMode = 'colorimetry' | 'aas';

export const MIN_CALIBRATION_STANDARDS = 5;
/** Relative tolerance for a learner concentration read from the fitted line. */
export const CALIBRATION_RELATIVE_TOLERANCE = 0.03;
export const ABSORBANCE_DECIMALS = 3;
/** Fixed deterministic noise pattern, multiplied by each problem's noise amplitude. */
export const NOISE_PATTERN: readonly number[] = [1, -0.75, 0.5, -1, 0.75, -0.5];
export const UNKNOWN_NOISE_FACTOR = 0.5;

export interface CalibrationProblem extends DataProvenance {
  id: string;
  mode: CalibrationMode;
  title: string;
  context: string;
  analyte: string;
  wavelengthNm: number;
  unit: string;
  /** Absorbance per concentration unit for this model instrument and cell. */
  slopePerUnit: number;
  slopeNote: string;
  standards: number[];
  /** Concentration in the original sample, before any dilution. */
  sampleConcentration: number;
  /** 1 means the sample was measured undiluted. */
  dilutionFactor: number;
  noiseAbsorbance: number;
}

import { CALIBRATION_PROBLEMS } from './instrumentalCalibrationCases';

export { CALIBRATION_PROBLEMS };

export const AAS_ELEMENT_SPECIFIC_EXPLANATION =
  'A hollow-cathode lamp made of the same element emits that element’s own narrow lines. Only ground-state atoms of that element absorb exactly those wavelengths, so other elements in the flame barely affect the reading.';

export function calibrationProblemsFor(mode: CalibrationMode) {
  return CALIBRATION_PROBLEMS.filter((problem) => problem.mode === mode);
}

const roundAbsorbance = (value: number) => Number(value.toFixed(ABSORBANCE_DECIMALS));

export interface CalibrationPoint {
  concentration: number;
  absorbance: number;
}

export function standardReadings(problem: CalibrationProblem): CalibrationPoint[] {
  return problem.standards.map((concentration, index) => ({
    concentration,
    absorbance: roundAbsorbance(
      problem.slopePerUnit * concentration +
        NOISE_PATTERN[index % NOISE_PATTERN.length] * problem.noiseAbsorbance,
    ),
  }));
}

/** Absorbance of the solution placed in the instrument (after any dilution). */
export function unknownAbsorbance(problem: CalibrationProblem): number {
  const measured = problem.sampleConcentration / problem.dilutionFactor;
  return roundAbsorbance(
    problem.slopePerUnit * measured + UNKNOWN_NOISE_FACTOR * problem.noiseAbsorbance,
  );
}

export interface CalibrationFit {
  slope: number;
  intercept: number;
  rSquared: number;
  minConcentration: number;
  maxConcentration: number;
  pointCount: number;
}

/** Least-squares line. Fails closed (null) below the minimum standards or for a flat line. */
export function fitCalibration(points: CalibrationPoint[]): CalibrationFit | null {
  if (points.length < MIN_CALIBRATION_STANDARDS) return null;
  const n = points.length;
  const meanX = points.reduce((sum, point) => sum + point.concentration, 0) / n;
  const meanY = points.reduce((sum, point) => sum + point.absorbance, 0) / n;
  let sxx = 0;
  let sxy = 0;
  let syy = 0;
  for (const point of points) {
    sxx += (point.concentration - meanX) ** 2;
    sxy += (point.concentration - meanX) * (point.absorbance - meanY);
    syy += (point.absorbance - meanY) ** 2;
  }
  if (sxx === 0 || syy === 0) return null;
  const slope = sxy / sxx;
  if (!(slope > 0)) return null;
  const concentrations = points.map((point) => point.concentration);
  return {
    slope,
    intercept: meanY - slope * meanX,
    rSquared: (sxy * sxy) / (sxx * syy),
    minConcentration: Math.min(...concentrations),
    maxConcentration: Math.max(...concentrations),
    pointCount: n,
  };
}

export interface CalibrationReading {
  absorbance: number;
  measuredConcentration: number;
  inRange: boolean;
  /** Original-sample concentration; null when the reading is outside the calibration range. */
  sampleConcentration: number | null;
}

export function readUnknown(problem: CalibrationProblem, fit: CalibrationFit): CalibrationReading {
  const absorbance = unknownAbsorbance(problem);
  const measuredConcentration = (absorbance - fit.intercept) / fit.slope;
  const inRange =
    measuredConcentration >= fit.minConcentration && measuredConcentration <= fit.maxConcentration;
  return {
    absorbance,
    measuredConcentration,
    inRange,
    sampleConcentration: inRange ? measuredConcentration * problem.dilutionFactor : null,
  };
}

const near = (value: number, target: number) =>
  Math.abs(value - target) <= Math.abs(target) * CALIBRATION_RELATIVE_TOLERANCE;

export type CalibrationAnswer = number | 'out-of-range';

export function formatConcentration(value: number) {
  return value >= 10 ? value.toFixed(1) : value >= 1 ? value.toFixed(2) : value.toFixed(3);
}

export function checkCalibrationAnswer(
  problem: CalibrationProblem,
  fit: CalibrationFit,
  answer: CalibrationAnswer,
) {
  const reading = readUnknown(problem, fit);
  const unit = problem.unit;
  const range = `${formatConcentration(fit.minConcentration)}–${formatConcentration(fit.maxConcentration)} ${unit}`;
  if (!reading.inRange) {
    const correct = answer === 'out-of-range';
    const belowRange = reading.measuredConcentration < fit.minConcentration;
    const remedy = belowRange
      ? 'The reading is below the lowest standard. Prepare lower standards, use a longer cell or a more sensitive method, then measure again.'
      : 'Dilute the sample so it falls between the standards and measure again.';
    return {
      correct,
      reading,
      feedback: correct
        ? `Correct: A = ${reading.absorbance.toFixed(3)} lies outside the calibrated range (${range}). The model reports no value. ${remedy}`
        : `No value can be reported. A = ${reading.absorbance.toFixed(3)} gives about ${formatConcentration(
            reading.measuredConcentration,
          )} ${unit}, outside the standards (${range}). Extending the line beyond the standards assumes Beer–Lambert behaviour that has not been checked.`,
    };
  }
  const expected = reading.sampleConcentration as number;
  const working = `c = (A − b) ÷ m = (${reading.absorbance.toFixed(3)} − ${fit.intercept.toFixed(4)}) ÷ ${fit.slope.toFixed(4)} = ${formatConcentration(
    reading.measuredConcentration,
  )} ${unit}`;
  const dilution =
    problem.dilutionFactor > 1
      ? `, then × ${problem.dilutionFactor} for the dilution = ${formatConcentration(expected)} ${unit}`
      : '';
  if (answer === 'out-of-range') {
    return {
      correct: false,
      reading,
      feedback: `This reading is inside the calibrated range (${range}), so a value can be reported: ${working}${dilution}.`,
    };
  }
  if (!Number.isFinite(answer) || answer <= 0) {
    return { correct: false, reading, feedback: 'Enter a positive concentration.' };
  }
  if (near(answer, expected)) {
    return {
      correct: true,
      reading,
      feedback: `Correct: ${working}${dilution}. The reading lies between the standards, so the line can be used.`,
    };
  }
  let hint = `Rearrange A = mc + b to c = (A − b) ÷ m. ${working}${dilution}.`;
  if (problem.dilutionFactor > 1 && near(answer, reading.measuredConcentration)) {
    hint = `That is the concentration of the diluted solution. Multiply by the dilution factor ${problem.dilutionFactor}: ${formatConcentration(expected)} ${unit}.`;
  } else if (near(answer, fit.slope * reading.absorbance * problem.dilutionFactor)) {
    hint = `You multiplied by the gradient. Divide instead: ${working}${dilution}.`;
  } else if (
    Math.abs(fit.intercept) > 0 &&
    near(answer, (reading.absorbance / fit.slope) * problem.dilutionFactor) &&
    !near((reading.absorbance / fit.slope) * problem.dilutionFactor, expected)
  ) {
    hint = `Subtract the intercept before dividing by the gradient: ${working}${dilution}.`;
  }
  return { correct: false, reading, feedback: hint };
}

// ---- Wavelength to display colour (generated from model data) ----

const GAMMA = 0.8;
const MAX_INTENSITY = 255;

/**
 * Approximate display colour for a visible wavelength (Bruton's piecewise method). Used only to
 * draw generated spectrum lines and swatches; every line also has a numeric label.
 */
export function wavelengthToRgb(wavelengthNm: number): string {
  const w = wavelengthNm;
  let r = 0;
  let g = 0;
  let b = 0;
  if (w >= 380 && w < 440) {
    r = -(w - 440) / (440 - 380);
    b = 1;
  } else if (w >= 440 && w < 490) {
    g = (w - 440) / (490 - 440);
    b = 1;
  } else if (w >= 490 && w < 510) {
    g = 1;
    b = -(w - 510) / (510 - 490);
  } else if (w >= 510 && w < 580) {
    r = (w - 510) / (580 - 510);
    g = 1;
  } else if (w >= 580 && w < 645) {
    r = 1;
    g = -(w - 645) / (645 - 580);
  } else if (w >= 645 && w <= 780) {
    r = 1;
  }
  let factor = 0;
  if (w >= 380 && w < 420) factor = 0.3 + (0.7 * (w - 380)) / (420 - 380);
  else if (w >= 420 && w <= 700) factor = 1;
  else if (w > 700 && w <= 780) factor = 0.3 + (0.7 * (780 - w)) / (780 - 700);
  const channel = (value: number) =>
    value === 0 ? 0 : Math.round(MAX_INTENSITY * (value * factor) ** GAMMA);
  return `rgb(${channel(r)}, ${channel(g)}, ${channel(b)})`;
}
